import React, { useCallback, useEffect, useState } from 'react';
import { PreviewFrame } from './PreviewFrame';
import { PreviewToolbar, ResponsiveMode, ZoomLevel } from './PreviewToolbar';

interface ArtifactPreviewProps {
  htmlContent: string;
  title?: string;
  className?: string;
  onError?: (error: string) => void;
  showEditor?: boolean;
  onToggleEditor?: () => void;
}

const ArtifactPreview: React.FC<ArtifactPreviewProps> = React.memo(({
  htmlContent,
  className,
  onError,
  showEditor = true,
  onToggleEditor,
}) => {
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(100);
  const [responsiveMode, setResponsiveMode] = useState<ResponsiveMode>('desktop');
  const [customWidth, setCustomWidth] = useState(1200);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Handle fullscreen change events (including ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleZoomChange = useCallback((zoom: ZoomLevel) => {
    setZoomLevel(zoom);
  }, []);

  const handleResponsiveModeChange = useCallback((mode: ResponsiveMode) => {
    setResponsiveMode(mode);
  }, []);

  const handleCustomWidthChange = useCallback((width: number) => {
    setCustomWidth(width);
  }, []);

  const handleFullscreenToggle = useCallback(async () => {
    try {
      if (isFullscreen) {
        await document.exitFullscreen();
      } else {
        const previewElement = document.querySelector('.artifact-preview');
        if (previewElement) {
          await previewElement.requestFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen toggle failed:', error);
      onError?.('Failed to toggle fullscreen mode');
    }
  }, [isFullscreen, onError]);

  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const handlePreviewError = useCallback((error: string) => {
    console.error('Preview frame error:', error);
    onError?.(error);
  }, [onError]);

  const getFrameWidth = useCallback(() => {
    switch (responsiveMode) {
      case 'mobile':
        return 375;
      case 'tablet':
        return 768;
      case 'desktop':
        return 1200;
      case 'custom':
        return customWidth;
      default:
        return 1200;
    }
  }, [responsiveMode, customWidth]);


  return (
    <div className={`flex flex-col h-full bg-background ${isFullscreen ? 'fixed inset-0 z-50' : ''} ${className || ''}`}>
      {/* Toolbar */}
      <div className="border-b border-border bg-card/50">
        <PreviewToolbar
          zoomLevel={zoomLevel}
          onZoomChange={handleZoomChange}
          responsiveMode={responsiveMode}
          onResponsiveModeChange={handleResponsiveModeChange}
          customWidth={customWidth}
          onCustomWidthChange={handleCustomWidthChange}
          isFullscreen={isFullscreen}
          onFullscreenToggle={handleFullscreenToggle}
          onRefresh={handleRefresh}
          showEditor={showEditor}
          onToggleEditor={onToggleEditor || (() => { })}
        />
      </div>

      {/* Preview Frame Container */}
      <div className="flex-1 overflow-auto bg-muted/30 p-4 flex items-center justify-center min-h-0">
        <div
          className="bg-background border border-border rounded-lg shadow-lg transition-all duration-200 h-full max-h-full flex flex-col"
          style={{
            width: getFrameWidth(),
            maxWidth: '100%',
            transform: `scale(${zoomLevel / 100})`,
            transformOrigin: 'center top',
          }}
        >
          <PreviewFrame
            key={refreshKey}
            htmlContent={htmlContent}
            onError={handlePreviewError}
            className="w-full flex-1 rounded-lg overflow-hidden"
          />
        </div>
      </div>

      {/* Loading overlay for fullscreen transitions */}
      {isFullscreen && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-background/90 backdrop-blur-sm border border-border rounded-lg px-3 py-1">
            <span className="text-sm text-muted-foreground">Press ESC to exit fullscreen</span>
          </div>
        </div>
      )}
    </div>
  );
});

ArtifactPreview.displayName = 'ArtifactPreview';

export default ArtifactPreview; 